import { useCallback, useState } from 'react';

import { InboxEntry } from '@this+that/models';
import log from 'loglevel';

import { formatInboxEntryContext } from './doBoxContextFormatter';
import { resolveApplyTarget } from './doBoxSourceDetector';
import { useSubmitDoBoxRuleFeedback } from '../../services/doBoxRules.service';

interface UseDoBoxConfigUpdateReturn {
  updateInstructions: (
    inboxEntry: InboxEntry,
    reason: string,
  ) => Promise<boolean>;
  isUpdating: boolean;
  updateError: Error | null;
}

export function useDoBoxConfigUpdate(): UseDoBoxConfigUpdateReturn {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<Error | null>(null);

  const { submitDoBoxRuleFeedback } = useSubmitDoBoxRuleFeedback();

  const updateInstructions = useCallback(
    async (inboxEntry: InboxEntry, reason: string): Promise<boolean> => {
      setIsUpdating(true);
      setUpdateError(null);

      try {
        const context = formatInboxEntryContext(inboxEntry);
        const target = resolveApplyTarget(inboxEntry);

        await submitDoBoxRuleFeedback({
          instructions: reason,
          target,
          context,
        });

        return true;
      } catch (error) {
        const errorInstance =
          error instanceof Error
            ? error
            : new Error('Failed to update DoBox instructions');

        setUpdateError(errorInstance);
        log.error('DoBox config update failed:', errorInstance);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [submitDoBoxRuleFeedback],
  );

  return {
    updateInstructions,
    isUpdating,
    updateError,
  };
}
